import * as React from "react"
import {Loader2, LucideIcon} from "lucide-react"

import {cn} from "@/lib/utils"

import {CtaButtonIcon} from "./CtaButtonIcon"
import {formatCtaCount} from "./ctaShared"

// `tabular-nums` keeps the digits monospaced so a count ticking 9 → 10 does not reflow its neighbours,
// and `formatCtaCount` bounds the label at 4 characters so a busy discussion cannot widen the action
// row until the `…` button wraps. Both are applied to the visible count AND to the invisible reserve
// snapshot, which must measure the same string it will later render.
const COUNT_CLASS = "button-count tabular-nums"

type CtaButtonContentProps = {
    icon: LucideIcon
    text?: string | null
    loadingText?: string | null
    buttonCount?: number | null
    isLoading: boolean
    displayTextOnMobile?: boolean
    resolvedActiveClass?: string
    iconBaseClass: string
}

type CtaButtonContentSnapshotProps = {
    icon: LucideIcon
    text?: string | null
    buttonCount?: number | null
    isLoading: boolean
    displayTextOnMobile?: boolean
    resolvedActiveClass?: string
    iconBaseClass: string
}

type CtaButtonReserveSnapshotProps = {
    text?: string | null
    buttonCount?: number | null
    isLoading: boolean
    displayTextOnMobile?: boolean
    iconBaseClass: string
}

function CtaButtonReserveSnapshot({
    text = null,
    buttonCount = null,
    isLoading,
    displayTextOnMobile = true,
    iconBaseClass,
}: CtaButtonReserveSnapshotProps) {
    const textClassName = cn("p-0", !displayTextOnMobile ? "hidden sm:inline" : "")

    return (
        <span className="inline-flex items-center gap-1 whitespace-nowrap">
            <span aria-hidden={true} className={cn(iconBaseClass, "block shrink-0")}/>

            {buttonCount != null && !isLoading ? (
                <span
                    aria-hidden={true}
                    className={cn(COUNT_CLASS, "cta-reserve-text")}
                    data-cta-reserve-text={formatCtaCount(buttonCount)}
                />
            ) : null}

            {text != null ? (
                <span
                    aria-hidden={true}
                    className={cn(textClassName, "cta-reserve-text")}
                    data-cta-reserve-text={text}
                />
            ) : null}
        </span>
    )
}

function CtaButtonContentSnapshot({
    icon: Icon,
    text = null,
    buttonCount = null,
    isLoading,
    displayTextOnMobile = true,
    resolvedActiveClass,
    iconBaseClass,
}: CtaButtonContentSnapshotProps) {
    const textClassName = cn("p-0", !displayTextOnMobile ? "hidden sm:inline" : "")

    return (
        <span className="inline-flex items-center gap-1 whitespace-nowrap">
            {isLoading ? (
                <Loader2 className={cn(iconBaseClass, "animate-spin")} aria-hidden={true}/>
            ) : (
                <CtaButtonIcon
                    icon={Icon}
                    resolvedActiveClass={resolvedActiveClass}
                    baseClass={iconBaseClass}
                />
            )}

            {buttonCount != null && !isLoading ? (
                // data-cta-count carries the exact integer beside the (possibly abbreviated) label, so
                // browser tests never have to parse "1.2K". The vote arrows carry it too (VoteContainer).
                <span className={COUNT_CLASS} data-cta-count={buttonCount}>{formatCtaCount(buttonCount)}</span>
            ) : null}

            {text != null ? (
                <span className={textClassName}>{text}</span>
            ) : null}
        </span>
    )
}

export function CtaButtonContent({
    icon,
    text = null,
    loadingText = null,
    buttonCount = null,
    isLoading,
    displayTextOnMobile = true,
    resolvedActiveClass,
    iconBaseClass,
}: CtaButtonContentProps) {
    const loadingSnapshotText = loadingText ?? text

    return (
        <span className="inline-grid grid-cols-1 grid-rows-1 items-center justify-items-center">
            <span
                aria-hidden="true"
                data-slot="cta-content-idle-reserve"
                className="invisible pointer-events-none col-start-1 row-start-1"
            >
                <CtaButtonReserveSnapshot
                    text={text}
                    buttonCount={buttonCount}
                    isLoading={false}
                    displayTextOnMobile={displayTextOnMobile}
                    iconBaseClass={iconBaseClass}
                />
            </span>
            <span
                aria-hidden="true"
                data-slot="cta-content-loading-reserve"
                className="invisible pointer-events-none col-start-1 row-start-1"
            >
                <CtaButtonReserveSnapshot
                    text={loadingSnapshotText}
                    buttonCount={buttonCount}
                    isLoading={true}
                    displayTextOnMobile={displayTextOnMobile}
                    iconBaseClass={iconBaseClass}
                />
            </span>
            <span data-slot="cta-content-current" className="col-start-1 row-start-1">
                <CtaButtonContentSnapshot
                    icon={icon}
                    text={isLoading ? loadingSnapshotText : text}
                    buttonCount={buttonCount}
                    isLoading={isLoading}
                    displayTextOnMobile={displayTextOnMobile}
                    resolvedActiveClass={resolvedActiveClass}
                    iconBaseClass={iconBaseClass}
                />
            </span>
        </span>
    )
}
